import { makeStyles, Button, StatusLabel, Tooltip, Typography } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import {
    spacing10,
    spacing20,
    spacing30,
    spacing40,
    fontWeightBold,
    colorBrandNeutral100,
    colorTextError,
    colorTextNeutral600,
    borderRadiusSmall,
    borderRadiusMedium,
    colorBackgroundAlertWarning,
    colorTextAlertWarning,
    colorBackgroundAlertNeutral,
    colorTextAlertNeutral,
} from '@ellucian/react-design-system/core/styles/tokens';
import { getGroupStatus, getLatestUploadDate, getLatestReviewerComment, getLatestApplicantComment, shouldShowReviewerMessage, shouldShowApplicantMessage } from '../../../utilities/fileHelpers';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';
import FileCard from './FileCard';
import ApplicantMessageInput from './ApplicantMessageInput';
import MessageHistory from './MessageHistory';

const useStyles = makeStyles({
    name: 'DocumentGroupCards'
})(() => ({
    mobileCard: {
        padding: spacing30,
        backgroundColor: colorBrandNeutral100,
        cursor: 'pointer',
        borderRadius: borderRadiusSmall,
        marginBottom: spacing30 },
    mobileCardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20,
        marginBottom: spacing20 },
    mobileCardContent: {
        marginLeft: spacing40,
        display: 'grid',
        gap: spacing20 },
    mobileLabel: {
        color: colorTextNeutral600,
        marginBottom: spacing20 },
    mobileExpandedContent: {
        marginTop: spacing20 },
    itemNameText: {
        fontWeight: fontWeightBold,
        textTransform: 'uppercase' },
    expandIcon: {
        transition: 'transform 0.3s' },
    expandIconRotated: {
        transform: 'rotate(90deg)' },
    expandIconHidden: {
        visibility: 'hidden' },
    emptyStateText: {
        fontStyle: 'italic',
        color: colorTextNeutral600 },
    emptyStateContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20 },
    infoIcon: {
        color: colorTextNeutral600,
        cursor: 'pointer' },
    documentCountText: {
        color: colorTextNeutral600 },
    documentLabel: {
        fontWeight: fontWeightBold,
        marginBottom: spacing20 },
    fileList: {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing20 },
    uploadButtonContainer: {
        marginTop: spacing20,
        display: 'flex',
        gap: spacing20,
        alignItems: 'center',
        justifyContent: 'flex-start' },
    uploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20
    },
    maxFilesAlert: {
        marginBottom: spacing40,
        display: 'flex',
        alignItems: 'center',
        gap: spacing20
    },
    maxFilesAlertIcon: {
        color: colorTextError,
        flexShrink: 0
    },
    maxFilesAlertText: {
        color: colorTextError
    },
    nonExpandableRow: {
        cursor: 'default' },
    reviewerMessageBox: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing20,
        padding: spacing30,
        backgroundColor: colorBackgroundAlertWarning,
        borderRadius: borderRadiusMedium,
        marginTop: spacing20 },
    reviewerMessageIcon: {
        color: colorTextAlertWarning,
        flexShrink: 0,
        fontSize: '1.25rem' },
    reviewerMessageLabel: {
        fontWeight: fontWeightBold },
    applicantMessageBox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing20,
        padding: spacing30,
        backgroundColor: colorBackgroundAlertNeutral,
        borderRadius: borderRadiusMedium,
        marginTop: spacing20 },
    applicantMessageIcon: {
        color: colorTextAlertNeutral,
        flexShrink: 0,
        fontSize: '1.25rem',
        marginTop: spacing10 },
    applicantMessageLabel: {
        fontWeight: fontWeightBold,
        color: colorTextNeutral600 },
    hiddenContent: {
        display: 'none' } }));

const DocumentGroupCards = ({ groups, expandedRows, onRowClick, onDeleteFile, onViewFile, onUploadFile, onResubmitQuestion, onSendMessage, locale, disabled = false, previewMode = false, hasChanges = false }) => {
    const { classes } = useStyles();
    const intl = useIntl();

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick(index);
        }
    };

    return (
        <div data-testid="DocumentGroupCards">
            {groups.map((group, index) => {
                const isExpanded = expandedRows[index];
                const status = getGroupStatus(group.files, intl, group.documentReviewStatus);
                const latestDate = getLatestUploadDate(group.files, locale);
                const hasFiles = group.files.length > 0;
                const isNotReceived = status.type === 'default';
                const accessibleLabel = `${group.label} ${status.text}`;
                const contentId = `document-group-content-${index}`;
                const activeFiles = group.files.filter(f => f?.status !== 'deleted' && f?.status !== 'error');
                const activeFileCount = activeFiles.length;
                const maxFiles = group.fileNumberLimit || 5; // Default to 5 if not specified
                const hasReachedFileLimit = activeFileCount >= maxFiles;
               
                // Check if any file in this group is currently uploading
                const hasUploadingFiles = group.files.some(f => f?.status === 'uploading');
                
                return (
                    <div key={index}>
                        <div 
                            className={(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? classes.mobileCard : `${classes.mobileCard} ${classes.nonExpandableRow}`}
                            onClick={(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? () => onRowClick(index) : undefined}
                            onKeyDown={(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? (e) => handleKeyDown(e, index) : undefined}
                            role={(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? 'button' : undefined}
                            tabIndex={(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? 0 : undefined}
                            aria-label={accessibleLabel}
                            aria-expanded={hasFiles ? isExpanded : undefined}
                            aria-controls={hasFiles ? contentId : undefined}
                        >
                            <div className={classes.mobileCardHeader}>
                                <Icon 
                                    name="chevron-right" 
                                    className={`${classes.expandIcon} ${isExpanded ? classes.expandIconRotated : ''} ${!(hasFiles || group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') ? classes.expandIconHidden : ''}`}
                                    aria-hidden="true"
                                />
                                <Typography className={classes.itemNameText}>
                                    {group.label}
                                </Typography>
                            </div>
                            <div className={classes.mobileCardContent}>
                                <div>
                                    <Typography variant="body2" className={classes.mobileLabel}>
                                        <FormattedMessage id="supportingDocumentsFileCount" />
                                    </Typography>
                                    {!hasFiles ? (
                                        <div className={classes.emptyStateContainer}>
                                            <Typography className={classes.emptyStateText}>
                                                <FormattedMessage id="supportingDocumentsNoDocumentUploaded" defaultMessage="No document uploaded" />
                                            </Typography>
                                            {isNotReceived && (
                                                <Tooltip title={intl.formatMessage({ id: 'supportingDocumentsNotReceivedHelper' })}>
                                                    <Icon name="info" className={classes.infoIcon} aria-hidden="true" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    ) : (
                                        <Typography>
                                            {group.files.length} <span className={classes.documentCountText}>{group.files.length === 1 ? 'document' : 'documents'}</span>
                                        </Typography>
                                    )}
                                </div>
                                <div>
                                    <Typography variant="body2" className={classes.mobileLabel}>
                                        <FormattedMessage id="supplementalItemListStatus" />
                                    </Typography>
                                    <StatusLabel text={status.text} type={status.type} />
                                </div>
                                <div>
                                    <Typography variant="body2" className={classes.mobileLabel}>
                                        <FormattedMessage id="supportingDocumentsUploadDate" />
                                    </Typography>
                                    <Typography>{latestDate}</Typography>
                                </div>
                            </div>
                        </div>
                        {(hasFiles || group.documentReviewStatus === 'clarificationNeeded') && (
                            <div 
                                id={contentId}
                                className={`${classes.mobileExpandedContent} ${!isExpanded ? classes.hiddenContent : ''}`}
                                role="region"
                                aria-label={intl.formatMessage({ id: 'supportingDocumentsUploadedDocuments' })}
                            >
                                {hasFiles && (
                                    <>
                                        <Typography className={classes.documentLabel}>
                                            <FormattedMessage id="supportingDocumentsUploadedDocuments" defaultMessage="Uploaded Documents" />
                                        </Typography>
                                        <div className={classes.fileList} role="list">
                                            {group.files.map(item => {
                                                const canDelete = onDeleteFile && group.documentReviewStatus === 'clarificationNeeded' && !hasUploadingFiles;
                                                const canView = onViewFile && group.documentReviewStatus === 'clarificationNeeded' && !hasUploadingFiles;
                                                return (
                                                    <FileCard 
                                                        key={item.id || item.documentId} 
                                                        file={item} 
                                                        locale={locale}
                                                        isGroupUploading={hasUploadingFiles}
                                                        onDelete={canDelete ? () => onDeleteFile(item, group.questionId) : undefined}
                                                        onView={canView ? () => onViewFile(item, group.questionId) : undefined}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                                {hasReachedFileLimit && group.documentReviewStatus === 'clarificationNeeded' && (
                                    <div 
                                        className={classes.maxFilesAlert}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        <Icon name="info" className={classes.maxFilesAlertIcon} aria-hidden="true" />
                                        <Typography variant="body2" className={classes.maxFilesAlertText}>
                                            <FormattedMessage 
                                                id="uploadQuestionMaxFilesMessage" 
                                                values={{ activeCount: activeFileCount, maxCount: maxFiles }}
                                            />
                                        </Typography>
                                    </div>
                                )}
                                {(onUploadFile || onResubmitQuestion) && (group.showUploadButton || group.documentReviewStatus === 'clarificationNeeded') && (
                                    <div className={classes.uploadButtonContainer}>
                                        {onUploadFile && group.showUploadButton && (
                                            <span>
                                                <Button 
                                                    onClick={() => {
                                                        onUploadFile(group.questionId);
                                                    }}
                                                    color="secondary"
                                                    size="small"
                                                    className={classes.uploadButton}
                                                    startIcon={<Icon name="upload" />}
                                                    disabled={hasUploadingFiles || disabled || previewMode || hasReachedFileLimit}
                                                    aria-label={(() => {
                                                        const baseLabel = intl.formatMessage({ id: 'uploadDocument' });
                                                        if (hasUploadingFiles) {
                                                            return `${baseLabel} - ${intl.formatMessage({ id: 'uploadDialogStatusUploading' })}`;
                                                        }
                                                        return baseLabel;
                                                    })()}
                                                >
                                                    <FormattedMessage id="uploadDocument" />
                                                </Button>
                                            </span>
                                        )}
                                        {group.documentReviewStatus === 'clarificationNeeded' && onResubmitQuestion && (
                                            <Button 
                                                onClick={() => {
                                                    onResubmitQuestion(group.questionId);
                                                }}
                                                color="secondary"
                                                size="small"
                                                className={classes.uploadButton}
                                                startIcon={<Icon name="send" />}
                                                disabled={hasUploadingFiles || disabled || previewMode || !hasChanges}
                                                aria-label={(() => {
                                                    const baseLabel = intl.formatMessage({ id: 'resubmitQuestion' });
                                                    return baseLabel;
                                                })()}
                                            >
                                                <FormattedMessage id="resubmitQuestion" />
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {shouldShowReviewerMessage(group.documentReviewStatus, group.documentReviewComments) && (
                                    <div className={classes.reviewerMessageBox}>
                                        <Icon name="warning" className={classes.reviewerMessageIcon} />
                                        <Typography variant="body2">
                                            <span className={classes.reviewerMessageLabel}>
                                                <FormattedMessage id="supportingDocumentsReviewerMessage" />
                                            </span>{' '}
                                            {getLatestReviewerComment(group.documentReviewComments)?.comment}
                                        </Typography>
                                    </div>
                                )}
                                {shouldShowApplicantMessage(group.documentReviewStatus, group.documentReviewComments) && (
                                    <div className={classes.applicantMessageBox}>
                                        <Icon name="user" className={classes.applicantMessageIcon} />
                                        <Typography variant="body2">
                                            <span className={classes.applicantMessageLabel}>
                                                <FormattedMessage id="supportingDocumentsYourMessage" />
                                            </span>{' '}
                                            {getLatestApplicantComment(group.documentReviewComments)?.comment}
                                        </Typography>
                                    </div>
                                )}
                                {group.documentReviewStatus === 'clarificationNeeded' && onSendMessage && (
                                    <ApplicantMessageInput
                                        questionId={group.questionId}
                                        onSendMessage={onSendMessage}
                                    />
                                )}
                                {group.documentReviewStatus === 'clarificationNeeded' && group.documentReviewComments && group.documentReviewComments.length > 0 && (
                                    <MessageHistory
                                        comments={group.documentReviewComments}
                                        locale={locale}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default DocumentGroupCards;
